import React, { useState, ReactNode } from 'react'
import { Box, IconButton, Typography, Menu, MenuItem } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useAppTheme } from '@/theme/ThemeContext'
import { useNavigate } from 'react-router-dom'

interface WidgetProps {
  title?: string
  height?: string | number
  width?: string | number
  children: ReactNode
  menuItems?: Array<{
    label: string
    action: string
    onClick?: () => void
  }>
  onMenuAction?: (action: string) => void
  expandable?: boolean
  className?: string
  expandIcon?: React.ReactNode | string
  expandIconSize?: number
  onExpandClick?: () => void
  expandHref?: string
  showMenuButton?: boolean
  minHeight?: string | number
  maxHeight?: string | number
}

const Widget: React.FC<WidgetProps> = ({
  title = 'Widget',
  height,
  width,
  children,
  menuItems = [],
  onMenuAction,
  expandable = true,
  className,
  expandIcon,
  expandIconSize = 18,
  showMenuButton = true,
  onExpandClick,
  expandHref,
  minHeight = '380px',
  maxHeight = '550px',
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const navigate = useNavigate()

  // Theme-aware colors
  const colors = {
    border: isDarkMode ? '#374151' : '#323B771A',
    background: isDarkMode ? '#202B40' : '#FAFBFD',
    titleText: isDarkMode ? '#FFFFFF' : '#003F61',
    iconColor: isDarkMode ? '#94A3B8' : '#6B7E9D',
    iconHoverBg: isDarkMode ? 'rgba(148,163,184,0.1)' : 'rgba(107,126,157,0.1)',
    iconHoverColor: isDarkMode ? '#E5E7EB' : '#5A6B85',
    menuBg: isDarkMode ? '#202B40' : '#FFFFFF',
    menuText: isDarkMode ? '#E5E7EB' : '#2D3748',
    menuHoverBg: isDarkMode ? 'rgba(148,163,184,0.15)' : 'rgba(107, 126, 157, 0.08)',
    menuDivider: isDarkMode ? 'rgba(148,163,184,0.2)' : 'rgba(107, 126, 157, 0.15)',
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleExpandClick = () => {
    if (expandable) {
      setIsExpanded(!isExpanded)
    }
  }

  const handleMenuItemClick = (action: string, customOnClick?: () => void) => {
    handleMenuClose()

    if (customOnClick) {
      customOnClick()
    } else if (onMenuAction) {
      onMenuAction(action)
    }

    // Default actions
    switch (action) {
      case 'fullscreen':
        setIsExpanded(!isExpanded)
        break
      default:
    }
  }

  // Default menu items if none provided
  const defaultMenuItems =
    menuItems.length > 0
      ? menuItems
      : [
          { label: 'Refresh', action: 'refresh' },
          { label: 'Settings', action: 'settings' },
          { label: 'Export', action: 'export' },
          { label: 'Fullscreen', action: 'fullscreen' },
        ]

  return (
    <Box
      className={className}
      sx={{
        flex: 1,
        border: `1px solid ${colors.border}`,
        borderRadius: '8px',
        backgroundColor: colors.background,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        position: isExpanded ? 'fixed' : 'relative',
        top: isExpanded ? 0 : 'auto',
        left: isExpanded ? 0 : 'auto',
        zIndex: isExpanded ? 9999 : 'auto',
        ...(height != null ? { height } : {}),
        ...(width != null ? { width } : {}),
        minHeight,
        minWidth: '200px',
        maxHeight,
        ...(isExpanded ? { width: '100vw', height: '100vh' } : {}),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: `1px solid ${colors.border}`,
          backgroundColor: colors.background,
          flexDirection: 'row-reverse',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: colors.titleText,
            textAlign: 'right',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '20px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '24px',
          }}
        >
          {title}
        </Typography>

        {/* Header Actions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexDirection: 'row-reverse',
          }}
        >
          {/* Expand/Collapse Button */}
          {expandable && (
            <IconButton
              onClick={() => {
                if (expandHref) {
                    navigate(expandHref)
                    return
                }
                if (onExpandClick) {
                  onExpandClick()
                  return
                }
                handleExpandClick()
              }}
              size="small"
              sx={{
                color: colors.iconColor,
                p: '6px',
                borderRadius: '50%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: colors.iconHoverBg,
                  color: colors.iconHoverColor,
                  transform: 'scale(1.05)',
                },
                '&:active': { transform: 'scale(0.95)' },
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                }}
              >
                {typeof expandIcon === 'string' ? (
                  <Box
                    component="img"
                    src={expandIcon}
                    alt=""
                    sx={{
                      width: expandIconSize ?? 18,
                      height: expandIconSize ?? 18,
                      display: 'block',
                      filter: isDarkMode ? 'brightness(0) invert(0.8)' : 'none',
                    }}
                  />
                ) : expandIcon ? (
                  expandIcon
                ) : (
                  <ExpandMoreIcon
                    sx={{ fontSize: expandIconSize ?? 18, fontWeight: 'bold' }}
                  />
                )}
              </Box>
            </IconButton>
          )}
          {showMenuButton && (
            <IconButton
              onClick={handleMenuClick}
              size="small"
              sx={{
                color: colors.iconColor,
                padding: '6px',
                borderRadius: '50%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: colors.iconHoverBg,
                  color: colors.iconHoverColor,
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <MoreVertIcon
                sx={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                }}
              />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ height: 'calc(100% - 60px)', padding: '20px' }}>
        {children}
      </Box>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: '200px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            borderRadius: '8px',
            padding: '8px 0',
            backgroundColor: colors.menuBg,
          },
        }}
      >
        {defaultMenuItems.map((item, index) => (
          <Box key={index}>
            <MenuItem
              onClick={() => handleMenuItemClick(item.action, item.onClick)}
              sx={{
                textAlign: 'right',
                direction: 'rtl',
                fontSize: '14px',
                fontWeight: 200,
                color: colors.menuText,
                '&:hover': {
                  backgroundColor: colors.menuHoverBg,
                },
                '&:active': {
                  backgroundColor: colors.menuHoverBg,
                },
              }}
            >
              {item.label}
            </MenuItem>
            {index < defaultMenuItems.length - 1 && (
              <Box
                sx={{
                  height: '1px',
                  backgroundColor: colors.menuDivider,
                  margin: '4px 16px',
                }}
              />
            )}
          </Box>
        ))}
      </Menu>
    </Box>
  )
}

export default Widget
